import { z } from '@repo/schema'
import { GetCompanyAccountSchema, GetCompanySchema, GetCompanyType } from '@repo/schema/company'
import { getDataWrapperSchema } from '@repo/schema/data-wrapper'
import { PaymentTermPeriodSchema, PaymentTermTriggerSchema } from '@repo/schema/payment-terms'
import {
  addPhoneWithCountryValidation,
  createPhoneNumberFieldSchema,
  OptionalPhoneCountryCodeSchema,
} from '@repo/schema/phone'
import { UpFetch } from '@repo/upfetch'

import { createProcedure } from '../../lib/procedure'

export const createHauliersRoute = (upFetch: UpFetch) => {
  const p = createProcedure(upFetch)
  const phoneInputSchema = z.object({
    phone: createPhoneNumberFieldSchema().optional(),
    phone_country: OptionalPhoneCountryCodeSchema.optional(),
  })
  const accountInputSchema = z
    .object({
      carrier_payment_days: z.number().int().min(1).max(365).nullable(),
      carrier_payment_period: PaymentTermPeriodSchema.nullable(),
      carrier_payment_trigger: PaymentTermTriggerSchema.nullable(),
      phone: createPhoneNumberFieldSchema().nullish(),
      phone_country: OptionalPhoneCountryCodeSchema.nullish(),
    })
    .partial()
    .passthrough()
    .superRefine((data, ctx) => {
      addPhoneWithCountryValidation(ctx, data.phone, data.phone_country)
    })
  const createCompanyAddressInputSchema = z
    .object({
      address: z.string().optional(),
      address_second_line: z.string().optional(),
      city: z.string().optional(),
      country: z.string().optional(),
      postcode: z.string().optional(),
      region: z.string().optional(),
    })
    .optional()

  return {
    account: {
      get: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'account'])

        .query(({ path, upFetch }) =>
          upFetch(path, {
            schema: getDataWrapperSchema(GetCompanyAccountSchema).transform(data => ({
              ...data,
            })),
          }),
        ),

      update: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'account'])
        .input(accountInputSchema)
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'PATCH',
            schema: getDataWrapperSchema(GetCompanyAccountSchema),
          }),
        ),
    },

    analytics: {
      get: p
        .path(z.object({ id: z.number() }), p => ['companies', 'hauliers', p.id, 'analytics'])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            schema: getDataWrapperSchema(
              z.object({
                active_loads_count: z.number(),
                completed_loads_count: z.number(),
              }),
            ),
          }),
        ),
    },

    company: {
      create: p
        .path('companies', 'hauliers')
        .input(
          phoneInputSchema
            .extend({
              address: createCompanyAddressInputSchema,
              country: z.string().optional(),
              email: z.email().optional(),
              notes: z.string().optional(),
              organisation_id: z.number().optional(),
              registration_number: z.string().optional(),
              source: z.enum(['kcharles', 'cargoking', 'cargoking-shipper']),
              title: z.string(),
              type: z.literal('haulier'),
              vat: z.string().optional(),
              website: z.string().optional(),
            })
            .superRefine((data, ctx) => {
              addPhoneWithCountryValidation(ctx, data.phone, data.phone_country)
            }),
        )
        .organisation('organisation_id')
        .mutation(({ input, organisationId, path, upFetch }) =>
          upFetch(path, {
            body: { ...input, organisation_id: organisationId },
            method: 'POST',
            schema: getDataWrapperSchema(
              GetCompanySchema.pick({
                id: true,
                title: true,
              }).extend({
                type: GetCompanyType.nullish(),
              }),
            ),
          }),
        ),
    },

    loadTypes: {
      list: p.path('load-types').query(({ path, upFetch }) =>
        upFetch(path, {
          schema: getDataWrapperSchema(
            z.array(
              z.object({
                id: z.number(),
                slug: z.string(),
                title: z.string(),
              }),
            ),
          ),
        }),
      ),
    },
  }
}
